import React, { useState, useRef, useEffect } from 'react';
import { Task, User } from '../lib/api';
import { TaskCard } from './TaskCard';
import { TaskForm } from './TaskForm';
import { SimpleBadge } from './ui/simple-badge';

interface TaskListProps {
  tasks: Task[];
  onStatusChange: (taskId: string, status: Task['status']) => void;
  onDelete: (taskId: string) => void;
  onCreate: (taskData: any) => void;
  userId: string;
  showLeadInfo?: boolean;
  title?: string;
  isLoading?: boolean;
  onReassign?: (taskId: string, userId: string) => void;
  teamMembers?: User[];
}

export const TaskList: React.FC<TaskListProps> = ({
  tasks,
  onStatusChange,
  onDelete,
  onCreate,
  userId,
  showLeadInfo = false,
  title = 'Tasks',
  isLoading = false,
  onReassign,
  teamMembers,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed' | 'overdue'>('all');
  const [sortBy, setSortBy] = useState<'dueDate' | 'priority' | 'title'>('dueDate');
  const addTaskBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!showForm) {
      addTaskBtnRef.current?.focus();
    }
  }, [showForm]);

  const filteredTasks = tasks.filter(task => {
    const isOverdue = new Date(task.dueDate) < new Date() && task.status !== 'COMPLETED';
    
    switch (filter) {
      case 'pending':
        return task.status === 'PENDING' && !isOverdue;
      case 'completed':
        return task.status === 'COMPLETED';
      case 'overdue':
        return isOverdue;
      default:
        return true;
    }
  });

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    switch (sortBy) {
      case 'dueDate':
        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      case 'priority': {
        const priorityOrder = { URGENT: 4, HIGH: 3, MEDIUM: 2, LOW: 1 };
        return (priorityOrder[b.priority] ?? 0) - (priorityOrder[a.priority] ?? 0);
      }
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  const getTaskStats = () => {
    const total = tasks.length;
    const completed = tasks.filter(t => t.status === 'COMPLETED').length;
    const overdue = tasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'COMPLETED').length;
    const pending = total - completed - overdue;

    return { total, completed, overdue, pending };
  };

  const stats = getTaskStats();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <div className="flex items-center space-x-2 mt-1">
            <SimpleBadge className="bg-blue-100 text-blue-800 border-blue-200">
              {stats.total} Total
            </SimpleBadge>
            <SimpleBadge className="bg-green-100 text-green-800 border-green-200">
              {stats.completed} Completed
            </SimpleBadge>
            <SimpleBadge className="bg-yellow-100 text-yellow-800 border-yellow-200">
              {stats.pending} Pending
            </SimpleBadge>
            {stats.overdue > 0 && (
              <SimpleBadge className="bg-red-100 text-red-800 border-red-200">
                {stats.overdue} Overdue
              </SimpleBadge>
            )}
          </div>
        </div>
        
        <button
          ref={addTaskBtnRef}
          onClick={() => setShowForm(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-haspopup="dialog"
        >
          + Add Task
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <label htmlFor="task-filter" className="text-sm font-medium text-gray-700">Filter:</label>
          <select
            id="task-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Tasks</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="overdue">Overdue</option>
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <label htmlFor="task-sort" className="text-sm font-medium text-gray-700">Sort by:</label>
          <select
            id="task-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="dueDate">Due Date</option>
            <option value="priority">Priority</option>
            <option value="title">Title</option>
          </select>
        </div>
      </div>

      {/* Task Form Modal */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Create New Task"
          onKeyDown={(e) => { if (e.key === 'Escape') setShowForm(false); }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Task</h3>
            <TaskForm
              onSubmit={(taskData) => {
                onCreate(taskData);
                setShowForm(false);
              }}
              onCancel={() => setShowForm(false)}
              userId={userId}
              isLoading={isLoading}
            />
          </div>
        </div>
      )}

      {/* Task List */}
      <div className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8" role="status" aria-label="Loading tasks">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden="true"></div>
            <span className="ml-2 text-gray-600">Loading tasks...</span>
          </div>
        ) : sortedTasks.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No tasks found.</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
            >
              Create your first task
            </button>
          </div>
        ) : (
          sortedTasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
              showLeadInfo={showLeadInfo}
              onReassign={onReassign}
              teamMembers={teamMembers}
            />
          ))
        )}
      </div>
    </div>
  );
}; 
